import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Link } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';
// Absolute origin used when persisting uploaded image URLs, so the value
// resolves correctly when read by a different-origin consumer (marketing site).
const ASSET_ORIGIN = API || (typeof window !== 'undefined' ? window.location.origin : '');

export default function ImageUploadField({ value, onChange, label = 'Image', hint }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'
  const [urlInput, setUrlInput] = useState(value || '');
  const fileRef = useRef();
  const token = localStorage.getItem('token');

  async function handleFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await fetch(`${API}/api/marketing/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onChange(`${ASSET_ORIGIN}${data.url}`);
    } catch (e) {
      alert('Image upload failed: ' + e.message);
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function applyUrl() {
    onChange(urlInput);
  }

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-sm font-semibold text-slate-700">{label}</label>
          <div className="flex gap-1 text-[11px]">
            <button onClick={() => setMode('upload')}
              className={`px-2 py-0.5 rounded-md font-semibold transition-colors ${mode === 'upload' ? 'bg-teal-100 text-teal-700' : 'text-slate-400 hover:text-slate-600'}`}>
              Upload
            </button>
            <button onClick={() => setMode('url')}
              className={`px-2 py-0.5 rounded-md font-semibold transition-colors ${mode === 'url' ? 'bg-teal-100 text-teal-700' : 'text-slate-400 hover:text-slate-600'}`}>
              URL
            </button>
          </div>
        </div>
      )}

      {mode === 'url' ? (
        <div className="flex gap-2">
          <input
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400"
            placeholder="https://example.com/image.jpg"
          />
          <button onClick={applyUrl}
            className="px-4 py-2 text-sm font-semibold bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-colors">
            Apply
          </button>
        </div>
      ) : (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`relative border-2 border-dashed rounded-2xl transition-all cursor-pointer min-h-[100px] flex items-center justify-center
            ${dragging ? 'border-teal-400 bg-teal-50' : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'}`}
        >
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={e => handleFile(e.target.files[0])} />
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <Loader2 size={22} className="text-teal-500 animate-spin" />
              <span className="text-xs text-slate-500 font-medium">Uploading…</span>
            </div>
          ) : value ? (
            <div className="relative w-full p-2">
              <img src={value} alt="Preview" className="max-h-40 max-w-full mx-auto rounded-xl object-cover" />
              <button
                onClick={e => { e.stopPropagation(); onChange(''); }}
                className="absolute top-3 right-3 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-6 px-4 text-center">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <Upload size={18} className="text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600">Drop an image or click to browse</p>
              <p className="text-xs text-slate-400">JPG, PNG, WebP, GIF · Max 5 MB</p>
            </div>
          )}
        </div>
      )}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}
